import React, { FC, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  GridView as GridViewIcon,
  List as ListIcon,
  Add as AddIcon,
  LocationOn as LocationOnIcon,
  RocketLaunch as RocketLaunchIcon,
  FileUpload as FileUploadIcon,
  FmdGoodOutlined as SiteIcon,
  Devices as DevicesIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { useAppState } from 'src/state-management/state-provider';
import SiteMapWithCompare from 'src/view/DMap/SiteMapWithCompare';
import { ProjectWithSites } from 'src/state-management/state';
import { Site, UserRole } from 'src/api/Api';
import ProjectDevicesDialog from 'src/view/Admin/components/ProjectDevicesDialog';
import ProjectToolsDialog from 'src/view/Admin/components/ProjectToolsDialog';
import ProjectSessionsDialog from 'src/view/Admin/components/ProjectSessionsDialog';
import { getProjectDeviceCustodyList, DeviceCustodyRecord } from 'src/api2/deviceCustodyApi';

const ProjectDetails: FC = () => {
  const { state } = useAppState();
  const navigate = useNavigate();
  const project = state.selectedProject?.projectDetails as ProjectWithSites | undefined;
  const isAdmin = state.user?.role?.includes(UserRole.Admin) ?? false;

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isDevicesDialogOpen, setIsDevicesDialogOpen] = useState(false);
  const [isToolsDialogOpen, setIsToolsDialogOpen] = useState(false);
  const [isSessionsDialogOpen, setIsSessionsDialogOpen] = useState(false);
  const [assignedDevices, setAssignedDevices] = useState<DeviceCustodyRecord[]>([]);

  useEffect(() => {
    if (project?.project_id) {
      getProjectDeviceCustodyList(project.project_id)
        .then((records) => {
          const safeRecords = Array.isArray(records) ? records : [];
          const active = safeRecords.filter(r => r && r.returned_at === null);
          setAssignedDevices(active);
        })
        .catch((err) => console.error('Failed to fetch project custody list:', err));
    }
  }, [project?.project_id]);

  const filteredSites = useMemo(() => {
    if (!project?.sites) return [];
    return project.sites.filter(site =>
      site.site_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [project?.sites, searchTerm]);

  if (!project) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading project details...</Typography>
      </Box>
    );
  }

  const handleSiteClick = (siteId: string) => {
    navigate(`/admin/project/${project.project_id}/site/${siteId}`);
  };

  return (
    <Box sx={{ p: 4, backgroundColor: '#0A0A0A', minHeight: '100vh', color: '#FFF' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{project.project_name}</Typography>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', color: '#999' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 16, color: '#10B981' }} />
              <Typography variant="body2">{project.country || 'MADHYA PRADESH, India'}</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              bgcolor: '#10B981',
              color: '#FFF',
              fontWeight: 700,
              borderRadius: '12px',
              textTransform: 'none',
              px: 3,
              py: 1.2,
              '&:hover': { bgcolor: '#059669' }
            }}
          >
            Create Site
          </Button>
          <Button
            variant="outlined"
            onClick={() => setIsToolsDialogOpen(true)}
            startIcon={<RocketLaunchIcon />}
            sx={{
              borderColor: '#333',
              color: '#FFF',
              borderRadius: '12px',
              textTransform: 'none',
              px: 3,
              '&:hover': { borderColor: '#10B981', color: '#10B981' }
            }}
          >
            Manage Tools
          </Button>
          <Button
            variant="outlined"
            onClick={() => setIsDevicesDialogOpen(true)}
            startIcon={<DevicesIcon />}
            sx={{
              borderColor: '#333',
              color: '#FFF',
              borderRadius: '12px',
              textTransform: 'none',
              px: 3,
              '&:hover': { borderColor: '#10B981', color: '#10B981' }
            }}
          >
            Manage Devices
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/admin/project/${project.project_id}/media`)}
            sx={{
              borderColor: '#333',
              color: '#FFF',
              borderRadius: '12px',
              textTransform: 'none',
              px: 3,
            }}
          >
            Manage Media
          </Button>
          {isAdmin && (
            <Button
              variant="outlined"
              onClick={() => setIsSessionsDialogOpen(true)}
              startIcon={<EventAvailableIcon />}
              sx={{
                borderColor: '#333',
                color: '#FFF',
                borderRadius: '12px',
                textTransform: 'none',
                px: 3,
                '&:hover': { borderColor: '#10B981', color: '#10B981' }
              }}
            >
              Manage Sessions
            </Button>
          )}
        </Box>
      </Box>

      {/* Stats Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: '#111', border: '1px solid #222', borderRadius: '16px' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, backgroundColor: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px' }}>
                <SiteIcon sx={{ color: '#10B981', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography sx={{ color: '#999', fontSize: 12 }}>Total Sites</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFF' }}>{project.sites?.length || 0}</Typography>
                <Typography sx={{ color: '#666', fontSize: 10 }}>Across this project</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: '#111', border: '1px solid #222', borderRadius: '16px' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, backgroundColor: 'rgba(163, 113, 247, 0.1)', borderRadius: '12px' }}>
                <RocketLaunchIcon sx={{ color: '#A371F7', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography sx={{ color: '#999', fontSize: 12 }}>Total Deployments</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFF' }}>18</Typography>
                <Typography sx={{ color: '#666', fontSize: 10 }}>Active and completed</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: '#111', border: '1px solid #222', borderRadius: '16px' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
                <FileUploadIcon sx={{ color: '#3B82F6', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography sx={{ color: '#999', fontSize: 12 }}>Files Uploaded</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFF' }}>24.8K</Typography>
                <Typography sx={{ color: '#666', fontSize: 10 }}>Across all sites</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: '#111', border: '1px solid #222', borderRadius: '16px' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, backgroundColor: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px' }}>
                <DevicesIcon sx={{ color: '#10B981', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography sx={{ color: '#999', fontSize: 12 }}>Assigned Devices</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFF' }}>{assignedDevices.length}</Typography>
                <Typography sx={{ color: '#666', fontSize: 10 }}>In project inventory</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Map Section */}
      <Box sx={{ mb: 4, borderRadius: '24px', overflow: 'hidden', border: '1px solid #222', height: '400px' }}>
        <SiteMapWithCompare
          sites={project.sites || []}
          requiredBounrdaryMarker={true}
          fitToBoundary={true}
        />
      </Box>

      {/* Sites Listing Section */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Search sites..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: 350,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#111',
              color: '#FFF',
              borderRadius: '12px',
              '& fieldset': { borderColor: '#222' },
              '&:hover fieldset': { borderColor: '#10B981' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#666' }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Typography sx={{ color: '#666', fontSize: '14px' }}>Sort by: <span style={{ color: '#FFF' }}>A to Z</span></Typography>
          <Box sx={{ display: 'flex', backgroundColor: '#111', borderRadius: '8px', p: 0.5, border: '1px solid #222' }}>
            <IconButton
              size="small"
              onClick={() => setViewMode('grid')}
              sx={{ color: viewMode === 'grid' ? '#10B981' : '#666', bgcolor: viewMode === 'grid' ? 'rgba(16, 185, 129, 0.1)' : 'transparent', borderRadius: '6px' }}
            >
              <GridViewIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setViewMode('list')}
              sx={{ color: viewMode === 'list' ? '#10B981' : '#666', bgcolor: viewMode === 'list' ? 'rgba(16, 185, 129, 0.1)' : 'transparent', borderRadius: '6px' }}
            >
              <ListIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {filteredSites.map((site) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={site.site_id}>
            <SiteCard site={site} onClick={() => handleSiteClick(site.site_id)} />
          </Grid>
        ))}
      </Grid>

      <ProjectDevicesDialog
        open={isDevicesDialogOpen}
        onClose={() => setIsDevicesDialogOpen(false)}
        projectId={project.project_id}
        onUpdate={(updatedActiveCustodies) => setAssignedDevices(updatedActiveCustodies)}
      />

      <ProjectToolsDialog
        open={isToolsDialogOpen}
        onClose={() => setIsToolsDialogOpen(false)}
        projectId={project.project_id}
        orgId={project.organisation_id}
        projectName={project.project_name}
      />

      {isAdmin && (
        <ProjectSessionsDialog
          open={isSessionsDialogOpen}
          onClose={() => setIsSessionsDialogOpen(false)}
          projectId={project.project_id}
          projectName={project.project_name}
        />
      )}
    </Box>
  );
};

const SiteCard: FC<{ site: Site; onClick: () => void }> = ({ site, onClick }) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        backgroundColor: '#111',
        border: '1px solid #222',
        borderRadius: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': { transform: 'translateY(-4px)', borderColor: '#10B981' }
      }}
    >
      <Box sx={{ position: 'relative', pt: '75%' }}>
        <CardMedia
          component="img"
          image="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=2560&auto=format&fit=crop"
          sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box sx={{ position: 'absolute', top: 12, left: 12 }}>
          <Chip
            label="ACTIVE"
            size="small"
            sx={{ bgcolor: '#10B981', color: '#FFF', fontWeight: 700, fontSize: '10px', height: '20px' }}
          />
        </Box>
      </Box>
      <CardContent sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 1, color: '#FFF' }}>{site.site_name}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <LocationOnIcon sx={{ fontSize: 14, color: '#666' }} />
          <Typography variant="caption" sx={{ color: '#999' }}>{'Kudremukh, Karna...'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <RocketLaunchIcon sx={{ fontSize: 14, color: '#666' }} />
          <Typography variant="caption" sx={{ color: '#999' }}>2 Deployments</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProjectDetails;
